/**
 * Контракт «активный суб-профиль сменился» на стороне клиента.
 *
 * Паспорт отбивает 401 любой запрос с токеном суб-профиля, который больше не активен на учётке
 * (профиль переключили здесь или на другом устройстве). Раньше клиент смотрел только на статус,
 * поэтому молча рефрешил токен и повторял запрос: данные приходили уже от НОВОГО профиля, а сторы
 * и UI оставались от старого — тихое смешение двух контекстов. Отличить это от «сессия мертва»
 * позволяет машинно-читаемый `code` в теле ответа.
 *
 * Лестница деградации (см. зонтичный репозиторий crypto-services,
 * .meta/docs/sub-profile-switching/solution.md, Ш3):
 *   1) быстрый путь — WS `activeUserChanged` → refresh → жёсткая перезагрузка;
 *   2) фолбэк — 401 с этим кодом → refresh → жёсткая перезагрузка;
 *   3) худший случай — не сошлись за отведённое число попыток → на логин.
 */

export const ACTIVE_PROFILE_CHANGED_CODE = 'active_profile_changed'

const RELOAD_GUARD_KEY = 'activeProfileReloadAttempt'

/** Сколько перезагрузок подряд допускаем, прежде чем признать, что механизм не сработал. */
const MAX_RELOAD_ATTEMPTS = 2

/** Окно, в пределах которого попытки считаются «подряд идущими». */
const ATTEMPT_WINDOW_MS = 60000

interface ReloadAttempt {
  n: number
  ts: number
}

export const isActiveProfileChanged = (error: any): boolean =>
  error?.response?.status === 401 &&
  error?.response?.data?.code === ACTIVE_PROFILE_CHANGED_CODE

const readAttempt = (): ReloadAttempt | null => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)

    return typeof parsed?.n === 'number' && typeof parsed?.ts === 'number' ? parsed : null
  } catch (e) {
    // Приватный режим / битое значение: теряем защиту от повторной перезагрузки,
    // но не ломаем обработку ошибки.
    return null
  }
}

/**
 * Исчерпан ли лимит перезагрузок.
 *
 * Отметка живёт со ВРЕМЕНЕМ и сама истекает. Булев флаг не подошёл бы с двух сторон: если снимать
 * его только в аварийной ветке — он останется навсегда и следующее штатное переключение сразу
 * уедет на логин; если снимать по любому успешному ответу — после перезагрузки успешные запросы
 * приходят раньше повторного 401, лимит недостижим, и клиент зацикливается на reload↔401.
 */
export const isReloadBudgetExhausted = (): boolean => {
  const attempt = readAttempt()
  if (!attempt) return false
  if (Date.now() - attempt.ts > ATTEMPT_WINDOW_MS) return false

  return attempt.n >= MAX_RELOAD_ATTEMPTS
}

export const markReloadAttempt = (): void => {
  const previous = readAttempt()
  const fresh = previous === null || Date.now() - previous.ts > ATTEMPT_WINDOW_MS
  const next: ReloadAttempt = { n: fresh ? 1 : (previous as ReloadAttempt).n + 1, ts: Date.now() }

  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(next))
  } catch (e) { /* storage недоступен — см. readAttempt */ }
}

export const clearReloadAttempt = (): void => {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch (e) { /* storage недоступен */ }
}
